<template>
	<div class="RebookingContingentCalendar" data-cy="contingentCalendar">
		<div class="calendar-header">
			<v-btn icon variant="text" size="small" data-cy="calendarPrev" :disabled="!canGoPrevious" @click="previousMonth"><v-icon>mdi-chevron-left</v-icon></v-btn>
			<v-select class="header-select month-select" :model-value="month" :items="monthItems" item-title="title" item-value="value"
				variant="plain" density="compact" hide-details data-cy="calendarMonthSelect" @update:model-value="setMonth" />
			<v-select class="header-select year-select" :model-value="year" :items="yearItems"
				variant="plain" density="compact" hide-details data-cy="calendarYearSelect" @update:model-value="setYear" />
			<v-btn icon variant="text" size="small" data-cy="calendarNext" :disabled="!canGoNext" @click="nextMonth"><v-icon>mdi-chevron-right</v-icon></v-btn>
		</div>
		<div class="calendar-body">
			<div class="calendar-grid" :class="{ 'is-loading': loading }">
				<div v-for="weekday in weekdays" :key="weekday" class="weekday">{{ weekday }}</div>
				<div v-for="cell in cells" :key="cell.key"
					class="day"
					:class="{
						'other-month': !cell.inMonth,
						selectable: cell.selectable,
						disabled: cell.inMonth && !cell.selectable,
						selected: cell.date === modelValue,
					}"
					:data-cy="cell.selectable ? 'calendarDay' : null"
					@click="cell.selectable && selectDate(cell.date)"
				>
					<span class="day-number">{{ cell.dayNumber }}</span>
					<span v-if="cell.contingentLabel" class="contingent" :class="{ 'sold-out': !cell.selectable || cell.soldOut }" :title="cell.contingentTitle">{{ cell.contingentLabel }}</span>
				</div>
			</div>
			<div v-if="loading" class="calendar-loading" data-cy="calendarLoading">
				<v-progress-circular indeterminate size="26" width="3" color="#00aeef" />
			</div>
		</div>
	</div>
</template>

<script>
// Lean month-grid calendar for the rebooking edit row. Purely presentational: contingent
// data comes in via the `days` prop ({ 'YYYY-MM-DD': slots[] }); month paging emits
// `update:month` so the parent can fetch that month's availabilities. The remaining-places
// numbers use the exact Service Designer formula (rebooking-utils slotFree).
import { dayHasFreeSlot, slotFree } from './rebooking-utils'

export default {
	name: 'RebookingContingentCalendar',
	props: {
		modelValue: String, // selected date YYYY-MM-DD
		time: String, // selected timeslot HH:mm — day numbers show THIS slot's contingent
		days: { type: Object, default: () => ({}) }, // date -> slots[]
		minDate: String, // first selectable date YYYY-MM-DD
		maxDate: String, // last date with possible availability (product availability window end)
		loading: Boolean,
	},
	data() {
		// open on a month that actually exists in the picker: the selected date's month when
		// it is within the availability window, otherwise the first selectable month. Opening on
		// an out-of-window month (e.g. a past booking) left the month <select> showing a raw
		// value like "2" until the user picked one manually.
		const view = this.initialView()
		return { year: view.year, month: view.month }
	},
	computed: {
		weekdays() {
			// monday-first, localized without needing translation keys (2024-01-01 is a Monday)
			const locale = this.$vuetify?.locale?.current ?? 'de'
			return Array.from({ length: 7 }, (_, i) => new Date(Date.UTC(2024, 0, i + 1)).toLocaleDateString(locale, { weekday: 'narrow' }))
		},
		monthItems() {
			const locale = this.$vuetify?.locale?.current ?? 'de'
			return Array.from({ length: 12 }, (_, i) => ({
				title: new Date(Date.UTC(2024, i, 1)).toLocaleDateString(locale, { month: 'long' }),
				value: i,
			})).filter(item => this.monthInBounds(this.year, item.value))
		},
		yearItems() {
			const firstYear = Number((this.minDate ?? new Date().toISOString()).substring(0, 4))
			const lastYear = this.maxDate ? Number(this.maxDate.substring(0, 4)) : firstYear + 2
			const years = []
			for (let year = firstYear; year <= lastYear; year++) years.push(year)
			return years
		},
		canGoPrevious() {
			return this.monthInBounds(this.month === 0 ? this.year - 1 : this.year, this.month === 0 ? 11 : this.month - 1)
		},
		canGoNext() {
			return this.monthInBounds(this.month === 11 ? this.year + 1 : this.year, this.month === 11 ? 0 : this.month + 1)
		},
		cells() {
			const firstOfMonth = new Date(Date.UTC(this.year, this.month, 1))
			// monday-first grid
			const startOffset = (firstOfMonth.getUTCDay() + 6) % 7
			const start = new Date(Date.UTC(this.year, this.month, 1 - startOffset))
			const cells = []
			for (let i = 0; i < 42; i++) {
				const current = new Date(start.getTime() + i * 86400000)
				const date = current.toISOString().substring(0, 10)
				const inMonth = current.getUTCMonth() === this.month
				const day = this.days[date]
				const hasContingent = !!day?.length
				// timeslot contingents are independent — never sum them across the day. Show the
				// selected timeslot's numbers; if that slot doesn't exist on this day (or no time
				// is selected), show the day's best slot as an upper bound for a single booking.
				const shown = hasContingent ? this.relevantSlot(day) : null
				const free = shown ? Math.max(slotFree(shown), 0) : 0
				const total = shown ? shown.total : 0
				// the day stays selectable when ANY slot has places — the user can still switch to
				// another timeslot after picking the date
				const selectable = inMonth
					&& (!this.minDate || date >= this.minDate)
					&& hasContingent
					&& dayHasFreeSlot({ slots: day })
				cells.push({
					key: date,
					date,
					dayNumber: current.getUTCDate(),
					inMonth,
					selectable,
					soldOut: hasContingent && free <= 0,
					contingentLabel: inMonth && hasContingent ? `${this.formatCount(free)}/${this.formatCount(total)}` : '',
					contingentTitle: inMonth && hasContingent ? `${free} / ${total}` : '',
				})
			}
			return cells
		},
	},
	mounted() {
		this.emitMonth()
	},
	methods: {
		initialView() {
			var candidate = this.modelValue || this.minDate || new Date().toISOString().substring(0, 10)
			var year = Number(candidate.substring(0, 4))
			var month = Number(candidate.substring(5, 7)) - 1
			if (this.monthInBounds(year, month)) return { year, month }
			// fall back to the first month at/after minDate (or the candidate) that is in bounds
			var floor = this.minDate || candidate
			year = Number(floor.substring(0, 4))
			month = Number(floor.substring(5, 7)) - 1
			for (var i = 0; i < 36; i++) {
				if (this.monthInBounds(year, month)) return { year, month }
				month++
				if (month > 11) { month = 0; year++ }
			}
			return { year: Number(candidate.substring(0, 4)), month: Number(candidate.substring(5, 7)) - 1 }
		},
		// the slot whose contingent a day cell displays: the selected timeslot when it exists on
		// that day, otherwise the slot with the most free places
		relevantSlot(slots) {
			if (this.time) {
				const matched = slots.find(slot => slot.time === this.time)
				if (matched) return matched
			}
			return slots.reduce((best, slot) => slotFree(slot) > slotFree(best) ? slot : best, slots[0])
		},
		// compact remaining/total label so large contingents (e.g. 1000/1000) fit the day cell
		formatCount(n) {
			if (n >= 10000) return Math.round(n / 1000) + 'k'
			if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
			return String(n)
		},
		// a month is within bounds when it overlaps [minDate month, maxDate]
		monthInBounds(year, month) {
			const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`
			const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
			const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
			if (this.minDate && monthEnd < this.minDate) return false
			if (this.maxDate && monthStart > this.maxDate) return false
			return true
		},
		selectDate(date) {
			this.$emit('update:modelValue', date)
		},
		setMonth(month) {
			this.month = month
			this.emitMonth()
		},
		setYear(year) {
			this.year = year
			// clamp the month if it falls outside the availability window in the new year
			if (!this.monthInBounds(year, this.month)) {
				const allowed = Array.from({ length: 12 }, (_, i) => i).filter(month => this.monthInBounds(year, month))
				if (allowed.length) this.month = allowed[0]
			}
			this.emitMonth()
		},
		previousMonth() {
			if (!this.canGoPrevious) return
			this.month--
			if (this.month < 0) { this.month = 11; this.year-- }
			this.emitMonth()
		},
		nextMonth() {
			if (!this.canGoNext) return
			this.month++
			if (this.month > 11) { this.month = 0; this.year++ }
			this.emitMonth()
		},
		emitMonth() {
			const from = `${this.year}-${String(this.month + 1).padStart(2, '0')}-01`
			const lastDay = new Date(Date.UTC(this.year, this.month + 1, 0)).getUTCDate()
			const until = `${this.year}-${String(this.month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
			this.$emit('update:month', { from, until })
		},
	},
}
</script>

<style scoped>
.RebookingContingentCalendar {
	background: white;
	border-radius: 8px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
	padding: 12px;
	width: 340px;
}
.calendar-header {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-bottom: 8px;
}
.header-select {
	flex: 0 0 auto;
	font-weight: 600;
}
.header-select :deep(.v-field__input) {
	padding-top: 0;
	padding-bottom: 0;
	min-height: 32px;
	font-weight: 600;
}
.month-select { width: 130px; }
.year-select { width: 90px; }
.calendar-body { position: relative; }
.calendar-grid {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	gap: 2px;
	transition: opacity .15s;
}
.calendar-grid.is-loading { opacity: .4; pointer-events: none; }
.calendar-loading {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}
.weekday {
	text-align: center;
	font-size: 12px;
	color: #8f8f8f;
	padding: 4px 0;
}
.day {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 42px;
	min-width: 0;
	padding: 2px 1px;
	border-radius: 6px;
	font-size: 14px;
	overflow: hidden;
}
.day.other-month { visibility: hidden; }
.day.disabled { color: #c4c4c4; cursor: not-allowed; }
.day.selectable { cursor: pointer; }
.day.selectable:hover { background: #e3f5fd; }
.day.selected { background: #00aeef; color: white; }
.day.selected .contingent { color: white; }
.contingent {
	font-size: 9.5px;
	color: #28500e;
	line-height: 1.1;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-variant-numeric: tabular-nums;
}
.contingent.sold-out { color: #a8a8a8; }
</style>
